import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, MapPin } from 'lucide-react';
import { projectsData } from '../mockData';

const ProjectDetail = () => {
  const { id } = useParams();
  const project = projectsData.find(p => p.id === parseInt(id));

  if (!project) {
    return <Navigate to="/projects" replace />;
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Back Button */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Button asChild variant="outline" className="border-[#2C2C2C] text-[#2C2C2C] hover:bg-[#F5F1E8]">
            <Link to="/projects">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Projects
            </Link>
          </Button>
        </div>
      </section>

      {/* Project Header */}
      <section className="py-12 bg-[#F5F1E8]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <span className="inline-block bg-[#2F4538] text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
              {project.type}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#2C2C2C] mb-4 font-poppins">
              {project.title}
            </h1>
            <div className="flex items-center text-gray-700">
              <MapPin className="w-5 h-5 mr-2" />
              <span>{project.location}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Image */}
      <section className="bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <img
            src={project.image}
            alt={project.title}
            className="w-full max-w-6xl mx-auto rounded-lg shadow-xl h-[500px] object-cover"
          />
        </div>
      </section>

      {/* Project Details */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-12">
            {/* Client Brief */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#2C2C2C] mb-4 font-poppins">
                Client Brief
              </h2>
              <p className="text-gray-700 leading-relaxed text-lg">
                {project.clientBrief}
              </p>
            </div>

            {/* Design Solution */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#2C2C2C] mb-4 font-poppins">
                Design Solution
              </h2>
              <p className="text-gray-700 leading-relaxed text-lg">
                {project.solution}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Project Gallery */}
      <section className="py-12 bg-[#F5F1E8]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-[#2C2C2C] mb-8 font-poppins text-center">
              Project Gallery
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {project.images.map((image, index) => (
                <div key={index} className="rounded-lg overflow-hidden shadow-lg">
                  <img
                    src={image}
                    alt={`${project.title} - Image ${index + 1}`}
                    className="w-full h-[400px] object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#2F4538] text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 font-poppins">
            Inspired by This Project?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let's create something beautiful for your home too.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button asChild size="lg" className="bg-white text-[#2F4538] hover:bg-gray-100 transition-all hover:scale-105">
              <Link to="/contact">Start Your Project</Link>
            </Button>
            <span className="text-lg">or call</span>
            <a href="tel:+919910837533" className="text-2xl font-bold hover:underline">
              +91 99108 37533
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetail;